/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { type AcesDealsVehicleAttributes } from '../interface';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { type VehicleSummary } from '@/types';

/**
 * Helper function to extract and format vehicle question IDs from preferred vehicle
 * @param preferredVehicle - The preferred vehicle data
 * @returns Formatted string of vehicle question IDs or empty string if none available
 */
const getPreferredVehicleQuestions = (preferredVehicle: VehicleSummary) => {
  const finalQueryParamArr: string[] = [];

  if (preferredVehicle.acesVehicleAttributes?.vehicleQuestionsIds) {
    Object.entries(preferredVehicle.acesVehicleAttributes.vehicleQuestionsIds).forEach(
      ([key, value]) => {
        if (value && (typeof value === 'string' || typeof value === 'number')) {
          finalQueryParamArr.push(`${key}:${value}`);
        }
      }
    );
  }

  return finalQueryParamArr.join('||');
};

/**
 * React hook to get ACES vehicle attributes for deals
 * @returns AcesDealsVehicleAttributes object or null if ACES is not enabled or no preferred vehicle
 */
export function useAcesDealsVehicleAttributes(): AcesDealsVehicleAttributes | null {
  const preferredVehicle = usePreferredVehicle();
  const { acesEnabled } = useAcesFlags();

  if (!acesEnabled || !preferredVehicle) {
    return null;
  }

  return getAcesDealsVehicleAttributes(preferredVehicle, acesEnabled);
}

/**
 * Server-side helper function to get ACES vehicle attributes for deals
 * @param preferredVehicle - The preferred vehicle data
 * @param acesEnabled - Flag indicating if ACES is enabled
 * @returns AcesDealsVehicleAttributes object or null if ACES is not enabled or no preferred vehicle
 */
export function getAcesDealsVehicleAttributes(
  preferredVehicle: VehicleSummary | null | undefined,
  acesEnabled: boolean
): AcesDealsVehicleAttributes | null {
  if (!acesEnabled || !preferredVehicle) {
    return null;
  }

  const vehicleAttributes: AcesDealsVehicleAttributes = {
    makeId: String(preferredVehicle.makeId ?? ''),
    modelId: String(preferredVehicle.modelId ?? ''),
    vehicleQuestions: getPreferredVehicleQuestions(preferredVehicle),
    vehicleTypeId: String(preferredVehicle.vehicleTypeId ?? ''),
    year: String(preferredVehicle.year),
  };

  // Remove falsy values from the object
  Object.keys(vehicleAttributes).forEach((key) => {
    if (!vehicleAttributes[key as keyof AcesDealsVehicleAttributes]) {
      delete vehicleAttributes[key as keyof AcesDealsVehicleAttributes];
    }
  });

  return vehicleAttributes;
}
